/**
 * CapacityBillableTime — onglet « Temps facturé » de Capacity Analysis.
 * Vue RÉALISÉ, base temps d'ouverture (OT) : YTD si l'année est en cours
 * (mois clos cumulés), FY si l'année est close. Toutes les heures bouclent à 100 %.
 * Barre empilée par ligne : facturable (vert) · non facturé (terracotta) ·
 * absences (gris hachuré) ; marqueur navy = cible de facturation.
 *   • TF/TO   = facturable réel ÷ OT  → rendement sur temps payé (le vert).
 *   • Disponible = Idle ÷ OT          → capacité non utilisée (TR − TF − NF − ABS).
 *   • Cible (TO) = TF cible ÷ OT = target_billable_pct (cible en % de l'OT).
 * SOURCE UNIQUE = la vue annuelle (getTimeEffective) : OT et TR par contrat et
 * calendrier, TF et cible saisis à l'année — la même que les onglets de saisie
 * et que le CCR. (Le moteur d'audit, calé sur les mois saisis, reste l'outil
 * de l'onglet Audit — retiré d'ici le 2026-09-01.)
 * 4 blocs : tuiles de synthèse · par pool · par personne · mensuel.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CardShell from '../../components/CardShell';
import { usePersistentValueLocal } from '../../hooks/usePersistentState';
import SegmentedToggle from '../../components/SegmentedToggle';
import PillFilter from '../../components/PillFilter';
import SearchField from '../../components/SearchField';
import FilterIcon from '../../components/FilterIcon';
import FilterCount from '../../components/FilterCount';
import { getTimeEffective } from '../../api/hrTimeEffective';
import { fmtH, fmtPct } from './CapacityAnalysisPage';

// Largeurs de colonnes partagées (table-layout: fixed) pour aligner les deux
// tables pool/personne : la piste de barre tombe au même x et à la même largeur.
/* DIX colonnes depuis le 2026-09-01 (le TO et le TR sont entrés dans la
   table) : la liste en portait huit, et les deux dernières se retrouvaient
   sans largeur — la table débordait de la page. */
const COL_W = ['15%', '10%', '8%', '10%', '9%', '8%', '7%', '8%', '7%', '18%'];
const ColGroup = () => <colgroup>{COL_W.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>;

// Couleurs de la barre = tokens charte (jamais de hex en dur) :
//   productif = sage (positif, plein) · structure = ardoise CLAIR (neutre, plein) ·
//   non-productif = terracotta CLAIR, en aplat (2026-08-27, remplace les
//   hachures : à la taille d'une barre de tableau elles moirent et captent
//   l'œil plus que le chiffre, alors que ce segment n'est qu'un reste).
const C_PROD = 'var(--color-sage)';
const C_STRUCT = 'color-mix(in srgb, var(--color-text-muted) 28%, #fff)';
const C_NONPROD = 'color-mix(in srgb, var(--color-terracotta) 45%, #fff)';

// Légende des couleurs de la barre — dans l'ORDRE des segments.
const Legend = () => (
  <div className="cbt-legend">
    <span><span className="cbt-sw" style={{ background: C_PROD }} />Productif</span>
    <span><span className="cbt-sw" style={{ background: C_NONPROD }} />Non productif</span>
    <span><span className="cbt-sw" style={{ background: C_STRUCT }} />H. indirectes</span>
  </div>
);

/* Barre empilée base TO — 3 segments qui somment à 100 %. TF et NF CÔTE À
   CÔTE (2026-09-01) : les deux se comparent, la structure (heures indirectes,
   gris clair) ferme la barre. Le taux TF s'écrit DANS le segment vert ; trop
   étroit, il le masque plutôt que de déborder. */
function ToBar({ prodPct, structPct, nonprodPct, cibleTO, labelPct }) {
  const pct = (x) => `${Math.max(0, Math.min(100, x * 100))}%`;
  return (
    <div className="ca2-tobar">
      <div className="ca2-tobar__track">
        <div className="ca2-tobar__seg" style={{ width: pct(prodPct), background: C_PROD }}>
          <span className="ca2-tobar__in">{fmtPct(labelPct != null ? labelPct : prodPct)}</span>
        </div>
        <div className="ca2-tobar__seg" style={{ width: pct(nonprodPct), background: C_NONPROD }} />
        <div className="ca2-tobar__seg" style={{ width: pct(structPct), background: C_STRUCT }} />
        {cibleTO != null && <div className="ca2-tobar__mk" style={{ left: pct(cibleTO) }} />}
      </div>
    </div>
  );
}

export default function CapacityBillableTime({ tenantId, year }) {
  const { t } = useTranslation();

  const [annualRows, setAnnualRows] = useState([]);   // source annuelle unique

  useEffect(() => {
    if (!tenantId) return;
    getTimeEffective(tenantId, year)
      .then(r => setAnnualRows(r?.data?.rows || []))
      .catch(() => setAnnualRows([]));
  }, [tenantId, year]);

  // Décomposition du temps d'ouverture (OT) en 3 parts sommant à 100 % :
  //   productif = TF · structure = OT − TR (corp+supervision+congés spéciaux) ·
  //   non-productif = TR − TF (capacité présente non facturée). Cible = TF cible ÷ OT.
  const derive = (o) => {
    const ot = o.ot;
    const structH  = Math.max(0, ot - o.tr);
    const nonprodH = Math.max(0, o.tr - o.tf);
    return {
      structH, nonprodH,
      tfTO:       ot > 0 ? o.tf / ot : 0,
      /* Taux rapportés au TEMPS OUVERT (2026-09-01) : c'est la base commune
         des deux — le TF et le NF se lisent sur la même échelle, et leur somme
         plus la structure fait 100 %. */
      nfTO:       ot > 0 ? nonprodH / ot : 0,
      // Taux en % du TR (hors structure) : production mesurée (TF) et cible (standard).
      prodTR:     o.tr > 0 ? o.tf / o.tr : 0,
      cibleTR:    o.hasCible && o.tr > 0 ? o.tfStd / o.tr : null,
      prodPct:    ot > 0 ? o.tf / ot : 0,
      structPct:  ot > 0 ? structH / ot : 0,
      nonprodPct: ot > 0 ? nonprodH / ot : 0,
      cibleTO: o.hasCible && ot > 0 ? o.tfStd / ot : null,
    };
  };

  const persons = useMemo(() => {
    /* SOURCE ANNUELLE UNIQUE (2026-09-01) : OT, TR, TF et cible viennent tous
       de la vue annuelle — celle que saisissent « Capacité pratique (TR) » et
       « Heures affectées », et que le CCR consomme. L'écran mélangeait un
       OT/TR issu du moteur d'audit, calé sur les seuls MOIS SAISIS (144 h chez
       un client n'ayant rempli qu'un mois), avec un TF annuel : la capacité
       pratique sortait plus petite que les heures facturées. Le moteur d'audit
       reste l'outil de l'onglet Audit, où la période partielle est le sujet. */
    return annualRows.filter(r => !r.is_overhead_pool).map(r => {
      const std = Number(r.standard_billable_hours) || 0;
      const raw = {
        ot: Number(r.ot_hours) || 0,
        tr: Number(r.tr_hours) || 0,
        tf: Number(r.billable_hours) || 0,
        tfStd: std, hasCible: std > 0,
      };
      return {
        key: r.person_id,
        person_name: `${r.first_name || ''} ${r.last_name || ''}`.trim() || '—',
        pool_name: r.cost_pool || 'Unassigned',
        ...raw, bill: raw.tf, ...derive(raw),
      };
    });
  }, [annualRows]);

  /* Filtres du standard de carte : les pastilles de groupe et la recherche
     RÉDUISENT les lignes. Les groupes sont donc agrégés sur les personnes
     retenues — un sous-total doit porter sur ce qu'on lit. */
  const [groupSel, setGroupSel] = useState(() => new Set());
  const [search, setSearch] = useState('');
  const fPersons = useMemo(() => {
    const q = search.trim().toLowerCase();
    return persons.filter(pp =>
      (groupSel.size === 0 || groupSel.has(pp.pool_name || 'Unassigned')) &&
      (!q || `${pp.person_name} ${pp.pool_name}`.toLowerCase().includes(q)));
  }, [persons, groupSel, search]);

  const pools = useMemo(() => {
    const m = {};
    for (const p of fPersons) {
      if (!(p.ot > 0)) continue;
      const k = p.pool_name || 'Unassigned';
      (m[k] || (m[k] = { name: k, n: 0, ot: 0, tr: 0, tf: 0, tfStd: 0, hasCible: false }));
      m[k].n += 1; m[k].ot += p.ot; m[k].tr += p.tr; m[k].tf += p.tf;
      if (p.hasCible) { m[k].tfStd += p.tfStd; m[k].hasCible = true; }
    }
    return Object.values(m).map(x => ({ ...x, bill: x.tf, ...derive(x) }));
  }, [fPersons]);

  // ── Tri des colonnes — défaut : TF/TO décroissant (taux les plus hauts en haut) ──
  const [poolSort, setPoolSort]     = useState({ key: 'tfTO', dir: -1 });
  const [personSort, setPersonSort] = useState({ key: 'tfTO', dir: -1 });
  /* UNE table, deux lectures (2026-08-27) : on regarde par groupe ou par
     personne, jamais les deux tables l'une sous l'autre. En vue Groupe, un
     clic sur le groupe déplie les personnes qui le composent. */
  /* Par PERSONNE au repos (2026-09-01) : c'est la lecture qu'on vient chercher,
     le groupe en est l'agrégat. Le choix est RETENU d'une visite à l'autre :
     qui travaille par groupe ne doit pas rebasculer à chaque venue. */
  const [view, setView] = usePersistentValueLocal(
    `cbt_view_${tenantId || ''}`, 'person');           // 'pool' | 'person'
  const [openPools, setOpenPools] = useState(() => new Set());
  const togglePool = (name) => setOpenPools(prev => {
    const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n;
  });
  const poolNames = useMemo(
    () => [...new Set(persons.map(p => p.pool_name || 'Unassigned'))].sort((a, b) => a.localeCompare(b, 'fr')),
    [persons]);
  const sortBy = (list, sort, g) => {
    if (!sort.key || !g[sort.key]) return list;
    return [...list].sort((a, b) => { const va = g[sort.key](a), vb = g[sort.key](b); if (va < vb) return -sort.dir; if (va > vb) return sort.dir; return 0; });
  };
  const arrow = (sort, k) => (sort.key === k ? (sort.dir > 0 ? ' ▲' : ' ▼') : '');
  /* Le libellé vit dans un SPAN : `max-width` sur un <th> n'est qu'un vœu en
     `table-layout: auto`, un entête long débordait sur sa voisine. `sub` porte
     la précision sur une seconde ligne, en plus petit (2026-09-01). */
  const SortTh = ({ sort, set, k, label, sub, cls, style, title }) => (
    <th className={`${cls} cah-sort`} title={title} style={{ cursor: 'pointer', ...style }}
        onClick={() => set(s => (s.key === k ? { key: k, dir: -s.dir } : { key: k, dir: -1 }))}>
      {/* Chaque entête porte une ligne de sous-titre, VIDE au besoin : tous
          les titres s'alignent ainsi sur la même ligne de base. */}
      <span className="cbt-th-lbl">{label}<br /><span className="cbt-th-sub">{sub || '\u00A0'}</span></span>{arrow(sort, k)}
    </th>
  );
  const viewPools = sortBy(pools, poolSort, {
    name: p => (p.name || '').toLowerCase(), n: p => p.n, ot: p => p.ot, tr: p => p.tr,
    struct: p => p.structH, bill: p => p.bill, nonprod: p => p.nonprodH,
    tfTO: p => p.tfTO, nfTO: p => p.nfTO, prodtr: p => p.prodTR, cibletr: p => p.cibleTR ?? -1,
  });
  const viewPersons = sortBy(fPersons, personSort, {
    name: p => (p.person_name || '').toLowerCase(), pool: p => (p.pool_name || '').toLowerCase(),
    ot: p => p.ot, tr: p => p.tr,
    struct: p => p.structH, bill: p => p.bill, nonprod: p => p.nonprodH,
    tfTO: p => p.tfTO, nfTO: p => p.nfTO, prodtr: p => p.prodTR, cibletr: p => p.cibleTR ?? -1,
  });

  // ── Total / moyenne : agrégat pondéré d'un sous-ensemble (base OT) ──
  // Pool → périmètre complet ; Personne → suit le filtre par pool.
  const aggTotal = (list) => {
    const acc = list.filter(p => p.ot > 0).reduce((a, p) => {
      a.ot += p.ot; a.tr += p.tr; a.tf += p.tf;
      if (p.hasCible) { a.tfStd += p.tfStd; a.hasCible = true; }
      a.n += 1;
      return a;
    }, { n: 0, ot: 0, tr: 0, tf: 0, tfStd: 0, hasCible: false });
    return { n: acc.n, ot: acc.ot, tr: acc.tr, bill: acc.tf, tfStd: acc.tfStd, ...derive(acc) };
  };
  const total = aggTotal(fPersons);
  const activeFilters = groupSel.size + (search.trim() ? 1 : 0);
  // Personnes d'un groupe, triées comme la table des personnes.
  const personsOf = (name) => sortBy(
    fPersons.filter(pp => (pp.pool_name || 'Unassigned') === name), personSort, {
      name: p => (p.person_name || '').toLowerCase(), pool: p => (p.pool_name || '').toLowerCase(),
      ot: p => p.ot, tr: p => p.tr,
      struct: p => p.structH, bill: p => p.bill, nonprod: p => p.nonprodH, tfTO: p => p.tfTO,
      nfTO: p => p.nfTO, prodtr: p => p.prodTR, cibletr: p => p.cibleTR ?? -1,
    });

  const HEAD = (sort, set) => (
    <thead><tr>
      <SortTh sort={sort} set={set} k="name" label={view === 'pool' ? 'Groupe' : 'Personne'} cls="th-left" />
      {view === 'pool'
        ? <SortTh sort={sort} set={set} k="n" label="Pers." cls="th-left" />
        : <SortTh sort={sort} set={set} k="pool" label="Groupe" cls="th-left" />}
      {/* La cascade se lit de gauche à droite : le temps ouvert, ce que la
          structure en prend, ce qu'il en reste (TR), puis ce qu'on en fait
          (2026-09-01). La colonne « Cible » est partie : le standard ne se
          saisit plus sur cet écran. */}
      <SortTh sort={sort} set={set} k="ot" label="Temps ouvert" sub="TO" cls="th-right" title="Heures disponibles, congés déduits" />
      <SortTh sort={sort} set={set} k="struct" label="Heures indirectes" sub="Gestion, Superv., Congés" cls="th-right" title={t('phrases:p_4e99241635')} />
      <SortTh sort={sort} set={set} k="tr" label="Capacité pratique" sub="TR" cls="th-right" title="TO moins les heures indirectes" />
      <SortTh sort={sort} set={set} k="bill" label="Heures TF" cls="th-right" title={t('phrases:p_74f32552f3')} />
      <SortTh sort={sort} set={set} k="tfTO" label="Taux TF" sub="% vs TO" cls="th-right" title="Heures TF ÷ temps ouvert" />
      <SortTh sort={sort} set={set} k="nonprod" label="Heures NF" cls="th-right" title={t('phrases:p_ee023d55f8')} />
      <SortTh sort={sort} set={set} k="nfTO" label="Taux NF" sub="% vs TO" cls="th-right" title="Heures NF ÷ temps ouvert" />
      <SortTh sort={sort} set={set} k="tfTO" label="Usage du TO" cls="th-center" />
    </tr></thead>
  );
  // Les six colonnes chiffrées sont les mêmes dans les deux vues.
  const NUMS = (r) => (
    <>
      <td className="ca2-td-num ca2-td-h ca2-td-muted">{fmtH(r.ot)}</td>
      <td className="ca2-td-num ca2-td-h ca2-td-muted">{fmtH(r.structH)}</td>
      <td className="ca2-td-num ca2-td-h">{fmtH(r.tr)}</td>
      <td className="ca2-td-num ca2-td-h">{fmtH(r.bill)}</td>
      <td className="ca2-td-num">{fmtPct(r.tfTO)}</td>
      <td className="ca2-td-num ca2-td-h ca2-td-muted">{fmtH(r.nonprodH)}</td>
      <td className="ca2-td-num ca2-td-muted">{fmtPct(r.nfTO)}</td>
      <td className="ca2-td-util"><ToBar prodPct={r.prodPct} structPct={r.structPct} nonprodPct={r.nonprodPct} cibleTO={r.cibleTO} labelPct={r.tfTO} /></td>
    </>
  );

  return (
    <CardShell
      className="cbt-card"
      flush
      title={<>Utilisation du temps d'ouverture <small>· {year}</small></>}
      viewControls={(
        <SegmentedToggle
          label="Vue" value={view} onChange={setView}
          options={[{ value: 'person', label: 'Personne' }, { value: 'pool', label: 'Groupe' }]}
        />
      )}
      filters={(
        <>
          <FilterIcon />
          <PillFilter
            label="Groupe" value={groupSel} onChange={setGroupSel}
            options={poolNames.map(n => ({ value: n, label: n }))}
          />
          <FilterCount count={activeFilters}
                       onClear={() => { setGroupSel(new Set()); setSearch(''); }} />
        </>
      )}
      search={<SearchField value={search} onChange={setSearch} placeholder="Personne ou groupe…" />}
      filterCount={activeFilters}
      collapsible storageKey="cout-prod-usage"
    >
      {/* La légende se pose juste au-dessus du tableau qu'elle explique. */}
      <div className="cbt-legend-wrap"><Legend /></div>
      <div className="ca2-table-wrap">
        <table className="ca2-table cbt-fixed" style={{ tableLayout: 'fixed' }}>
          <ColGroup />
          {view === 'pool' ? HEAD(poolSort, setPoolSort) : HEAD(personSort, setPersonSort)}
          <tbody>
            {view === 'pool' && viewPools.map(g => {
              const open = openPools.has(g.name);
              return (
                <React.Fragment key={g.name}>
                  <tr className="cbt-grp">
                    {/* Toute la cellule déplie le groupe : viser un chevron
                        n'est pas une cible raisonnable. */}
                    <td className="ca2-td-pool cbt-td-click" role="button" tabIndex={0}
                        aria-expanded={open} title="Voir les personnes"
                        onClick={() => togglePool(g.name)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePool(g.name); }
                        }}>
                      <span className={`cbt-chev${open ? ' is-open' : ''}`} aria-hidden>{open ? '▾' : '▸'}</span>
                      {g.name}
                    </td>
                    <td className="ca2-td-pool ca2-td-muted">{g.n}</td>
                    {NUMS(g)}
                  </tr>
                  {open && personsOf(g.name).map(pp => (
                    <tr key={pp.key} className="cbt-sub">
                      <td className="ca2-td-pool cbt-td-sub">{pp.person_name}</td>
                      <td className="ca2-td-pool ca2-td-muted" />
                      {NUMS(pp)}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            {view === 'person' && viewPersons.map(pp => (
              <tr key={pp.key}>
                <td className="ca2-td-pool">{pp.person_name}</td>
                <td className="ca2-td-pool ca2-td-muted">{pp.pool_name}</td>
                {NUMS(pp)}
              </tr>
            ))}
            <tr className="cah-avg-row">
              <td className="ca2-td-pool">Total / moyenne</td>
              <td className="ca2-td-pool">{view === 'pool' ? total.n : ''}</td>
              <td className="ca2-td-num ca2-td-h">{fmtH(total.ot)}</td>
              <td className="ca2-td-num ca2-td-h">{fmtH(total.structH)}</td>
              <td className="ca2-td-num ca2-td-h">{fmtH(total.tr)}</td>
              <td className="ca2-td-num ca2-td-h">{fmtH(total.bill)}</td>
              <td className="ca2-td-num">{fmtPct(total.tfTO)}</td>
              <td className="ca2-td-num ca2-td-h">{fmtH(total.nonprodH)}</td>
              <td className="ca2-td-num">{fmtPct(total.nfTO)}</td>
              <td className="ca2-td-util"><ToBar prodPct={total.prodPct} structPct={total.structPct} nonprodPct={total.nonprodPct} cibleTO={total.cibleTO} labelPct={total.tfTO} /></td>
            </tr>
          </tbody>
        </table>
      </div>
    </CardShell>
  );
}
